import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextField,
  Grid,
  Stack,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  viewDataById,
  createNewData,
  updateDataById,
  deleteDataById,
} from '../../services/ftp.service';
import ConfirmPopup from '../common/confirm-popup.component';
import Iconify from '../common/iconify.component';

const AddEditFtpServers = ({ dataId, rowData, onCancel, onSuccess }) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      name: '',
      host: '',
      fileUrl: '',
      username: '',
      password: '',
      sourcePath: '/www/new',
      destinationPath: '/www/encoded',
      extensions: 'mkv,flv,avi,mts,mov,wmv,amv,mp4,mpg,mpeg,m4v',
      apiToken:
        'Q7bcupwu9v48dUA8FCDMZw7L2ZqoUOfH$ZWMyMDkyNjctNTA1ZS00NDYzLTk5YTktYWU3ZDc2YmY0ZjU0',
      apiLogin: 'javalawyer@gmail.com',
    },
  });

  const [loading, setLoading] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const handleDeleteData = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    deleteDataById(dataId)
      .then(() => {
        setShowDeleteConfirmation(false);
        onSuccess('FTP server deleted successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
      });
  };

  const saveNewData = (payload) => {
    createNewData(payload)
      .then(() => {
        onSuccess('New FTP server created successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const updateExistingData = (payload) => {
    updateDataById(dataId, payload)
      .then(() => {
        onSuccess('FTP server settings updated successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    if (dataId) {
      updateExistingData(data);
    } else {
      saveNewData(data);
    }
  };

  useEffect(() => {
    if (!dataId) {
      setLoading(false);
      return;
    }
    setLoading(true);

    // Reset form to remove form validation error
    reset();

    viewDataById(dataId)
      .then((res) => {
        // Fill form values
        setValue('name', res.data?.name || '');
        setValue('host', res.data?.host || '');
        setValue('fileUrl', res.data?.fileUrl || '');
        setValue('username', res.data?.username || '');
        setValue('password', res.data?.password || '');
        setValue('sourcePath', res.data?.sourcePath || '');
        setValue('destinationPath', res.data?.destinationPath || '');
        setValue('extensions', res.data?.extensions?.join(',') || '');
        setValue('apiToken', res.data?.apiToken || '');
        setValue('apiLogin', res.data?.apiLogin || '');

        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [dataId]);

  return (
    <>
      {loading && <Loading />}

      {!loading && (
        <>
          <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...maxLengthValidation(50),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Server Name"
                      type="text"
                      error={!!errors.name}
                      helperText={errors?.name?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} />

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="host"
                  control={control}
                  rules={{ ...validationRules.REQUIRED }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Host"
                      type="text"
                      error={!!errors.host}
                      helperText={errors?.host?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="fileUrl"
                  control={control}
                  rules={{ ...validationRules.REQUIRED }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="CDN URL"
                      type="text"
                      error={!!errors.fileUrl}
                      helperText={errors?.fileUrl?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="username"
                  control={control}
                  rules={{ ...validationRules.REQUIRED }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Username"
                      type="text"
                      error={!!errors.username}
                      helperText={errors?.username?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="password"
                  control={control}
                  rules={{ ...validationRules.REQUIRED }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      error={!!errors.password}
                      helperText={errors?.password?.message || null}
                      fullWidth
                      variant="filled"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              title={
                                !showPassword
                                  ? 'Show password'
                                  : 'Hide password'
                              }
                            >
                              <Iconify
                                icon={
                                  !showPassword
                                    ? 'eva:eye-fill'
                                    : 'eva:eye-off-fill'
                                }
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="sourcePath"
                  control={control}
                  rules={{ ...validationRules.REQUIRED }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Source Path"
                      type="text"
                      error={!!errors.sourcePath}
                      helperText={errors?.sourcePath?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="destinationPath"
                  control={control}
                  rules={{ ...validationRules.REQUIRED }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Destination Path"
                      type="text"
                      error={!!errors.destinationPath}
                      helperText={errors?.destinationPath?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="extensions"
                  control={control}
                  rules={{ ...validationRules.REQUIRED }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Extensions (Comma-Seperated)"
                      type="text"
                      error={!!errors.extensions}
                      helperText={errors?.extensions?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="apiToken"
                  control={control}
                  rules={{ ...validationRules.REQUIRED }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="API Token"
                      type="text"
                      error={!!errors.apiToken}
                      helperText={errors?.apiToken?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="apiLogin"
                  control={control}
                  rules={{ ...validationRules.REQUIRED }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="API Login"
                      type="text"
                      error={!!errors.apiLogin}
                      helperText={errors?.apiLogin?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mt={1}
          >
            <Grid>
              <Button
                type="submit"
                form="add-edit-form"
                color="primary"
                variant="contained"
                disabled={loading || formSubmitted}
                sx={{ marginRight: 1 }}
              >
                {!dataId ? 'Save New' : 'Update Data'}
              </Button>
              {!!dataId && (
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={() => {
                    onCancel();
                    reset();
                  }}
                  disabled={formSubmitted}
                >
                  Cancel
                </Button>
              )}
            </Grid>
            {!!dataId && (
              <Grid>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => setShowDeleteConfirmation(true)}
                  disabled={formSubmitted}
                >
                  Delete
                </Button>
              </Grid>
            )}
          </Stack>
        </>
      )}

      {showDeleteConfirmation && (
        <ConfirmPopup
          title="Delete FTP Server"
          message={`Do you want to delete ${rowData.name} FTP server? You can not undo this action!`}
          onCancel={() => setShowDeleteConfirmation(false)}
          onSuccess={() => handleDeleteData()}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

AddEditFtpServers.propTypes = {
  dataId: PropTypes.number.isRequired,
  rowData: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddEditFtpServers;
